import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  DEFAULT_CLUB_ROLES,
  defaultRoleById,
  reconcileSystemRolePermissions,
  type ClubRole,
} from '@/core/models/club-role'
import { logger } from '@/core/logger'
import { StaffRepository } from '../api/staff-repository'
import { ClubRolesRepository } from '../api/club-roles-repository'
import { useAuthStore } from '@/core/auth/auth-store'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { useIsPlatformAdmin } from '@/core/auth/use-is-admin'
import { usePreviewSession } from '@/features/preview-session/hooks/use-preview-session'
import { can, roleTier, tierMeets, type RoleTier } from '../domain/role-gating'
import type { ClubPermission } from '@/core/models/staff'

export const currentRoleKeys = {
  byUser: (clubId: string, uid: string) => ['current-role', clubId, uid] as const,
}

interface CurrentRoleResult {
  /** Effective role used for permission checks. For platform admins this is the
   * synthetic `owner` role (full access) even if the user has no staff entry
   * in this club. Use this for `can()` / `meets()` / tier gating. */
  role: ClubRole | null
  /** Actual role from the club's staff subcollection — null if the user has no
   * staff entry (the typical case for a platform admin triaging a club).
   * Use this for DISPLAY only; never for permission checks. A user has AT MOST
   * ONE staff role per club. */
  staffRole: ClubRole | null
  /** Coarse tier derived from the effective role. */
  tier: RoleTier | null
  /** Permission check. */
  can: (permission: ClubPermission) => boolean
  /** Minimum-tier gate. */
  meets: (required: RoleTier) => boolean
  isLoading: boolean
  isError: boolean
}

/**
 * Resolves the current user's effective role in the current club.
 *
 * Reads:
 *   1. `clubs/{clubId}/staff` where `uid == currentUid` → staff member's roleId
 *   2. `clubs/{clubId}/roles/{roleId}` → role permissions
 *
 * Falls back to `DEFAULT_CLUB_ROLES` if the roles subcollection is empty (e.g. a
 * freshly created club hasn't had its system roles seeded yet).
 *
 * **Platform admin passthrough**: when the current user is a platform admin
 * (`platform_config/admins` entry), the hook returns a synthetic `owner` role
 * with every permission — even when they have no staff doc in the impersonated
 * club. Lets admins triage, edit and recover any club's data without needing
 * to be added to its staff list.
 */
export function useCurrentRole(): CurrentRoleResult {
  const user = useAuthStore((s) => s.user)
  const clubId = useCurrentClubStore((s) => s.clubId)
  const uid = user?.uid ?? null
  const { isAdmin, isLoading: adminLoading } = useIsPlatformAdmin()
  // Preview-token visitors get the same synthetic-owner passthrough that
  // platform admins get. The intent of a preview link is "show me the
  // whole product as the future owner would see it" — gating views by
  // the visitor's missing staff doc walls them off from analytics,
  // settings, billing etc. and turns the demo into a frustration tour.
  // Read-only enforcement still lives elsewhere: Firestore rules reject
  // every write from a `preview-*` uid, and BookingFormPanel + every
  // other write-bearing UI surface checks `previewSession.active` to
  // disable the controls. The passthrough only unlocks VIEWS.
  const previewSession = usePreviewSession()

  const staffRepo = useMemo(() => (clubId ? new StaffRepository(clubId) : null), [clubId])
  const rolesRepo = useMemo(() => (clubId ? new ClubRolesRepository(clubId) : null), [clubId])

  // Always resolve the user's real staff entry, even for platform admins —
  // an admin MAY also be on the club's staff list (rare, but legitimate), and
  // we need to know that to render the correct role badge in the UI. Staff
  // lookup is one small doc + the roles list, cached 60 s.
  const query = useQuery({
    queryKey: clubId && uid ? currentRoleKeys.byUser(clubId, uid) : ['current-role', 'none'],
    queryFn: async () => {
      if (!staffRepo || !rolesRepo || !uid) return null
      const members = await staffRepo.findByUid(uid)
      const me = members[0]
      if (!me) return null
      const roles = await rolesRepo.listAll()
      const canWriteRoles = me.role === 'owner'
      // Self-heal every system role against code defaults
      // (`reconcileSystemRolePermissions`): untouched roles snap to the exact
      // default set (adds missing + strips stray keys), customised roles get
      // new default keys merged additively. The owner's sign-in reconciles +
      // persists ALL system roles — not just her own — because Firestore rules
      // allow only owner/admin to write `roles/{id}`, so manager/receptionist
      // docs would otherwise drift forever. Each write only fires when that
      // role actually changed, so a steady-state owner login is zero writes.
      if (canWriteRoles) {
        for (const r of roles) {
          const { merged: healed, changed } = reconcileSystemRolePermissions(r)
          if (!changed) continue
          rolesRepo
            .update(r.id, {
              permissions: healed.permissions,
              permissionsSchemaVersion: healed.permissionsSchemaVersion,
            })
            .catch((err) => logger.e('role permissions self-heal failed', err))
        }
      }
      const roleDoc = roles.find((r) => r.id === me.role)
      // Fail CLOSED: when a staff member's `role` matches neither a loaded
      // `roles/{id}` doc nor an in-code system role, resolve to null (→ no
      // access, gates deny) — NEVER to DEFAULT_CLUB_ROLES[0], which is the
      // omnipotent OWNER role. The old owner fallback was a privilege
      // escalation (audit M8): an unresolvable custom role (doc deleted /
      // dropped from the snapshot) silently became a de-facto owner, and
      // because booking/payment Firestore rules are coarse (isClubMember),
      // those writes would actually succeed. A null role surfaces as
      // "sin acceso" and prompts the owner to fix the assignment.
      const resolved = roleDoc ?? defaultRoleById(me.role) ?? null
      if (!resolved) return null
      // Always reconcile the current user's effective role in-memory so `can()`
      // is correct this session even before any write-back lands (and for
      // non-owner sessions, which never write back).
      const { merged } = reconcileSystemRolePermissions(resolved)
      return merged
    },
    enabled: !!staffRepo && !!rolesRepo && !!uid,
    // No `staleTime` — staff role changes need to apply instantly. Mutations
    // that touch a staff doc (`useUpdateStaff` etc.) invalidate this key so
    // the next render refetches; without staleTime, a freshly-invalidated
    // query is always considered stale and refetches on remount/focus too.
  })

  // Owner-passthrough: applies for platform admins AND preview-token
  // visitors. Both get the synthetic `owner` role for permission gates
  // (write enforcement is elsewhere — Firestore rules + previewSession-
  // aware UI). `can()` already short-circuits `role.id === 'owner'` to
  // always-true.
  const ownerRole = DEFAULT_CLUB_ROLES.find((r) => r.id === 'owner') ?? null
  const staffRole = query.data ?? null
  const useOwnerPassthrough = isAdmin || previewSession.active
  const role = useOwnerPassthrough ? (staffRole ?? ownerRole) : staffRole
  const tier = roleTier(role)

  // `query.isLoading` is FALSE when the query is disabled (clubId / uid not
  // yet resolved) — TanStack v5 semantics: isLoading = isPending && isFetching.
  // Treating that as "loaded with no role" causes RoleGate to redirect valid
  // users away from manager-tier routes during the brief window between auth
  // resolving and ClubGate setting the clubId. Surface "still resolving" as
  // isLoading=true in that case so RoleGate waits.
  //
  // Same flicker-protection for preview visitors: `previewSession` starts in
  // `isLoading: true / active: false` until the first onIdTokenChanged fires,
  // and during that window we'd briefly compute `role = staffRole = null` →
  // owner-only pages (Rendimiento etc.) flash the "no access" wall before
  // the passthrough kicks in. Wait for previewSession to settle.
  const stillResolving = !clubId || !uid

  return {
    role,
    staffRole,
    tier,
    can: (permission) => can(role, permission),
    meets: (required) => tierMeets(tier, required),
    isLoading: stillResolving || query.isLoading || adminLoading || previewSession.isLoading,
    isError: query.isError,
  }
}
